import { Component, effect, inject, OnDestroy } from '@angular/core';
import { computed } from '@angular/core';
import { Store } from '@ngrx/store';
import { AppStore } from '@aaa/emember/store-types';
import {
  getExecutionError,
  getIsLoadingExecution,
  getJoinAccountDetails,
  getJoinFormConfirmedMembers,
  getJoinFormPage,
  JoinActions,
} from '@aaa/emember/store-join';
import { RequestErrorType } from '../../../../store/generic-errors';
import { AnalyticsActions } from '../../../../store/analytics';
import { ClubApp } from '../../../../types';
import { toSignal } from '@angular/core/rxjs-interop';
import { getAccountUrls } from '../../../../constants/constants';
import { MembershipCardDetailListComponent } from '../../../share/membership-card-detail-list/membership-card-detail-list';
import { ButtonLinkComponent } from '../../../share/ava-link/ava-link.component';
import { ButtonComponent } from '../../../share/ava-button/ava-button.component';
import { MembershipUpdatePendingNoticeComponent } from '../../../share/ava-membership-update-pending-notice.component';
import { clubOption } from '../../../../signals';

@Component({
  selector: 'ava-join-confirmation',
  standalone: true,
  host: { class: 'tw-m-auto tw-block tw-w-full tw-max-w-5xl tw-pb-4 -lg:tw-p-4 -md:tw-p-1' },
  template: `
    @if (!isLoadingExecution()) {
      <div class="tw-m-auto tw-text-center">
        @switch (page()) {
          @case ('join') {
            @switch (clubOption().clubId) {
              @case (ClubApp.Shelby) {
                <p class="tw-text-[32px] tw-font-normal">Welcome to AAA!</p>
                <p class="tw-px-0 tw-py-2 tw-text-left tw-text-[18px] tw-font-extralight">
                  We're excited to have you on board. Look out for a confirmation email regarding your purchase.
                </p>
                @if (executionError()?.type === RequestErrorType.MembershipError) {
                  <ava-membership-update-pending-notice />
                }
                <p class="tw-px-0 tw-py-2 tw-text-left tw-text-[18px] tw-font-semibold">Important Information:</p>
                <ul class="tw-list-none tw-pl-4 tw-text-left">
                  <li class="tw-px-0 tw-py-2 tw-text-[18px] tw-font-extralight">
                    1. <span class="tw-font-semibold">Immediate Benefits:</span>
                    Upon joining, you can start using basic services like discounts and travel assistance right away.
                  </li>
                  <li class="tw-px-0 tw-py-2 tw-text-[18px] tw-font-extralight">
                    2. <span class="tw-font-semibold">Pre-Existing Conditions:</span>
                    Please note that we do not cover any emergency road services for issues that existed before your
                    membership purchase.
                  </li>
                  <li class="tw-px-0 tw-py-2 tw-text-[18px] tw-font-extralight">
                    3. <span class="tw-font-semibold">Plus and Premier Membership:</span>
                    If you've purchased a Plus or Premier membership, the extended roadside assistance benefits will
                    become available 30 days after your purchase.
                  </li>
                </ul>
                <p class="tw-px-0 tw-py-4 tw-text-[18px] tw-font-extralight">
                  Thank you for choosing us, and we're happy to support you on the road!
                </p>
              }
              @case (ClubApp.SouthJersey) {
                <p class="tw-text-5xl tw-font-normal">Thank you for becoming a member!</p>
                <p class="tw-text-[32px] tw-font-normal">Welcome to AAA.</p>
                <p class="tw-py-1 tw-text-left tw-text-[18px] tw-font-semibold">Here's What Comes Next:</p>
                @if (executionError()?.type === RequestErrorType.MembershipError) {
                  <ava-membership-update-pending-notice />
                }
                <ul class="tw-list-none tw-pl-4 tw-text-left">
                  <li class="tw-py-1 tw-text-[18px] tw-font-extralight">
                    1. Look for an email confirming your purchase.
                  </li>
                  <li class="tw-py-1 tw-text-[18px] tw-font-extralight">
                    2. You'll receive an email shortly to download your Digital AAA Membership Card to your Digital
                    Wallet.
                  </li>
                  <li class="tw-py-1 tw-text-[18px] tw-font-extralight">
                    3.
                    <ava-link class="" href="https://aaa.com/mobile"> Download the AAA Mobile App</ava-link>
                    to request roadside assistance, find discounts, plan your next trip, and more – all from your phone.
                  </li>
                </ul>
                <p class="tw-py-1 tw-text-left tw-text-base tw-font-extralight tw-italic">
                  AAA Plus and AAA Premier Membership benefits are available 7 days after payment is received.
                </p>
                @if (executionError()?.type === RequestErrorType.MembershipError) {
                  <br />
                  <p class="tw-pb-4 tw-text-[18px] tw-font-extralight">
                    Your payment was successful and Member Service representative will be reaching out shortly with your
                    membership number via email. Please allow 48 hours for the membership account to be updated. If you
                    have any questions, please email us or call
                    <ava-link href="tel:{{ clubOption().phoneNumber }}">
                      {{ clubOption().phoneNumberView }}
                    </ava-link>
                  </p>
                }
              }
              @case (ClubApp.MidStates) {
                <p class="tw-text-5xl tw-font-normal">Thank You!</p>
                <p class="tw-text-[32px] tw-font-normal">Welcome to AAA.</p>
                <p class="tw-pb-4 tw-text-[32px] tw-font-normal">We're happy to have you in the club.</p>
                @if (executionError()?.type === RequestErrorType.MembershipError) {
                  <p class="tw-pb-4 tw-text-xl tw-font-extralight">
                    Your payment was successful and Member Service representative will be reaching out shortly with your
                    membership number via email. Please allow 48 hours for the membership account to be updated. If you
                    have any questions, please email us or call
                    <ava-link href="tel:{{ clubOption().phoneNumber }}">
                      {{ clubOption().phoneNumberView }}
                    </ava-link>
                  </p>
                } @else if (!executionError()) {
                  <p class="tw-text-xl tw-font-extralight">Look for an email soon confirming your purchase.</p>
                  <p class="tw-pb-4 tw-text-xl tw-font-extralight">
                    If you are a AAA Basic member, road service benefits are available 72 hours after payment is
                    received. If you are a AAA Plus or AAA Premier member, Basic roadside benefits are available
                    immediately after membership purchase and AAA Plus and AAA Premier membership extended roadside
                    benefits are available 72 hours after payment is received.
                  </p>
                }
              }
              @case (ClubApp.Colorado) {
                <p class="tw-text-5xl tw-font-normal">Thank You!</p>
                <p class="tw-text-[32px] tw-font-normal">Welcome to AAA.</p>
                <p class="tw-pb-4 tw-text-[32px] tw-font-normal">We're happy to have you in the club.</p>
                @if (executionError()?.type === RequestErrorType.MembershipError) {
                  <ava-membership-update-pending-notice />
                }
                <div class="tw-flex tw-flex-col tw-items-start tw-justify-start">
                  <p class="tw-mb-4 tw-text-start tw-text-[18px] tw-font-extralight">
                    Login to your AAA Colorado account to view your account, check your renew date, upgrade your
                    membership, renew your membership, add household members, or request roadside assistance.
                    <!--                    , or-->
                    <!--                    <ava-link [href]="accountUrls.accountMobileLoginUrl"> login to our App</ava-link>-->
                  </p>
                  <!--                  <p class="tw-mb-4 tw-text-start tw-text-[18px] tw-font-extralight">-->
                  <!--                    If you are already a member of AAA Colorado but haven't accessed it before online, you can-->
                  <!--                    <ava-link [href]="accountUrls.accountLoginUrl">set up your account here</ava-link>-->
                  <!--                    .-->
                  <!--                  </p>-->
                  <!--                  <p class="tw-mb-4 tw-text-start tw-text-[18px] tw-font-extralight">-->
                  <!--                    If you are a member of a different AAA club outside of Colorado, please visit-->
                  <!--                    <ava-link href="http://aaa.com/stop">AAA.com/stop</ava-link>-->
                  <!--                    to find your club’s website.-->
                  <!--                  </p>-->
                  <div class="tw-mb-4 tw-flex tw-items-center tw-gap-2 tw-text-start md:tw-flex-row -md:tw-flex-col">
                    <a [href]="accountUrls.createAccountUrl" target="_blank">
                      <ava-button> Create Your Colorado Online Account</ava-button>
                    </a>
                    <!--                    <a [href]="accountUrls.accountLoginUrl" target="_blank">-->
                    <!--                      <ava-button> Account Login</ava-button>-->
                    <!--                    </a>-->
                  </div>
                </div>
              }
              @case (ClubApp.Hoosier) {
                <p class="tw-text-5xl tw-font-normal">Thank You!</p>
                <p class="tw-text-[32px] tw-font-normal">Welcome to AAA.</p>
                <p class="tw-pb-4 tw-text-[32px] tw-font-normal">We're happy to have you in the club.</p>
                @if (executionError()?.type === RequestErrorType.MembershipError) {
                  <p class="tw-pb-4 tw-text-xl tw-font-extralight">
                    Your payment was successful and Member Service representative will be reaching out shortly with your
                    membership number via email. Please allow 48 hours for the membership account to be updated. If you
                    have any questions, please email us or call
                    <ava-link href="tel:{{ clubOption().phoneNumber }}">
                      {{ clubOption().phoneNumberView }}
                    </ava-link>
                  </p>
                } @else if (!executionError()) {
                  <p class="tw-text-xl tw-font-extralight">Look for an email soon confirming your purchase.</p>
                  <p class="tw-pb-4 tw-text-xl tw-font-extralight">
                    Roadside assistance is available 72 hours after purchase. Same-day AAA Classic service is available
                    for a $75 instant service fee. Instant service is not available for monthly billing plans.
                  </p>
                }
              }
              @case (ClubApp.Northway) {
                <p class="tw-text-5xl tw-font-normal">Thank You!</p>
                <p class="tw-text-[32px] tw-font-normal">Welcome to AAA.</p>
                <p class="tw-pb-4 tw-text-[32px] tw-font-normal">We're happy to have you in the club.</p>
                @if (executionError()?.type === RequestErrorType.MembershipError) {
                  <p class="tw-pb-4 tw-text-xl tw-font-extralight">
                    Your payment was successful and Member Service representative will be reaching out shortly with your
                    membership number via email. Please allow 48 hours for the membership account to be updated. If you
                    have any questions, please email us or call
                    <ava-link href="tel:{{ clubOption().phoneNumber }}">
                      {{ clubOption().phoneNumberView }}
                    </ava-link>
                  </p>
                } @else if (!executionError()) {
                  <p class="tw-pb-4 tw-text-lg tw-font-extralight">
                    Upon joining AAA Northway or adding AAA Plus coverage upon renewal, there will be a 3-day wait
                    period for AAA Plus emergency road service benefits. Classic emergency road service benefits will be
                    rendered and additional costs payable to the service provider. Upgraded coverage (AAA Plus, AAA Plus
                    RV, AAA Premier) can be added or removed your membership only at the time of renewal.
                  </p>
                  <p class="tw-pb-4 tw-text-lg tw-font-extralight">
                    New members and members renewing 11 or more days after their expiration date will incur additional
                    fees such as enrollment costs and a non-refundable same day Emergency Road Service charge.
                  </p>
                }
              }
              @default {
                <p class="tw-text-5xl tw-font-normal">Thank You!</p>
                <p class="tw-text-[32px] tw-font-normal">Welcome to AAA.</p>
                <p class="tw-pb-4 tw-text-[32px] tw-font-normal">We're happy to have you in the club.</p>
                @if (executionError()?.type === RequestErrorType.MembershipError) {
                  <p class="tw-pb-4 tw-text-xl tw-font-extralight">
                    Your payment was successful and Member Service representative will be reaching out shortly with your
                    membership number via email. Please allow 48 hours for the membership account to be updated. If you
                    have any questions, please email us or call
                    <ava-link href="tel:{{ clubOption().phoneNumber }}">
                      {{ clubOption().phoneNumberView }}
                    </ava-link>
                  </p>
                } @else if (!executionError()) {
                  <p class="tw-text-xl tw-font-extralight">Look for an email soon confirming your purchase.</p>
                  <p class="tw-pb-4 tw-text-xl tw-font-extralight">
                    Basic road service benefits are available immediately after membership purchase.
                    <br />
                    @switch (clubOption().clubId) {
                      @case (ClubApp.Northampton) {
                        AAA Northampton County has a 10 day waiting period for Plus and Premier services.
                      }
                      @default {
                        AAA Plus and AAA Premier membership extended roadside benefits are available 72 hours after
                        payment is received.
                      }
                    }
                  </p>
                }
              }
            }
          }
          @case ('gift') {
            @if (executionError()?.type === RequestErrorType.MembershipError) {
              <div class="tw-text-5xl tw-font-normal">Thank You!</div>
              <div class="tw-pb-4 tw-text-xl tw-font-extralight">
                Your payment was successful and Member Service representative will be reaching out shortly with your new
                membership number via email. Please allow for 48hrs for the membership number to be created. If you have
                any questions, please call
                <ava-link href="tel:{{ clubOption().phoneNumber }}">
                  {{ clubOption().phoneNumberView }}
                </ava-link>
                . Thanks again for giving the gift of AAA.
              </div>
            } @else if (!executionError()) {
              <div class="tw-text-[60px] tw-font-normal">Thank You!</div>
              <div class="tw-pb-4 tw-text-xl tw-font-extralight">
                Your gift recipient membership will be active immediately and their Membership card will be mailed to
                them within 2 weeks. If you have any questions, please call
                <ava-link href="tel:{{ clubOption().phoneNumber }}">
                  {{ clubOption().phoneNumberView }}
                </ava-link>
                . Thanks again for giving the gift of AAA.
              </div>
            }
          }
        }
        <ava-membership-card-detail-list [accountDetails]="accountDetails()" [members]="members()" />
      </div>
    }
  `,
  imports: [
    MembershipCardDetailListComponent,
    ButtonLinkComponent,
    ButtonComponent,
    MembershipUpdatePendingNoticeComponent,
  ],
})
export class JoinConfirmationComponent implements OnDestroy {
  readonly accountUrls = getAccountUrls();
  private readonly store = inject(Store<AppStore>);

  readonly ClubApp = ClubApp;
  readonly RequestErrorType = RequestErrorType;

  readonly members = toSignal(this.store.select(getJoinFormConfirmedMembers), { initialValue: [] });
  readonly accountDetails = toSignal(this.store.select(getJoinAccountDetails), { initialValue: null });
  readonly page = toSignal(this.store.select(getJoinFormPage));
  readonly isLoadingExecution = toSignal(this.store.select(getIsLoadingExecution), { initialValue: false });
  readonly executionError = toSignal(this.store.select(getExecutionError));
  readonly clubOption = computed(() => clubOption());

  constructor() {
    effect(() => {
      const shouldDispatch = !!this.page() && !this.isLoadingExecution() && !!this.members().length;

      if (!shouldDispatch) return;

      this.store.dispatch(
        AnalyticsActions.viewedPage({
          eventName: `${this.page()}-confirmation`,
          input: {
            state: 'CO',
            itemQuantity: this.members().length,
            itemPrimaryQuantity: 1,
            itemMembershipLevel: this.members()[0].membershipLabel,
            itemPrimaryMembershipNumber: this.members()[0].membershipNumber,
            itemAssociatesQuantity: this.members().length - 1,
            pageName: `${this.page()}-confirmation`,
          },
        }),
      );

      this.store.dispatch(
        AnalyticsActions.pageLoaded({
          feature: this.page() || '',
          target: 'confirmation',
          error: this.executionError(),
        }),
      );
    });
  }

  ngOnDestroy() {
    this.store.dispatch(JoinActions.resetConfirmedMembers());
  }
}
